import { ReactNode, useCallback, useMemo, memo, useState, useEffect, useRef } from 'react'
import {
  Box,
  Flex,
  useDisclosure,
} from '@chakra-ui/react'
import { useAuthStore } from '@/store/auth'
import { useNavigate } from 'react-router-dom'
import { Header } from '@/components/layout/Header'
import { Sidebar } from '@/components/layout/Sidebar'
import { useThemeColors } from '@/hooks/useThemeColors'

interface AdminLayoutProps {
  children: ReactNode
}

function AdminLayoutComponent({ children }: AdminLayoutProps) {
  // Only extract the specific color values we need to prevent re-renders
  const bgColor = useThemeColors().bg
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return typeof window !== 'undefined' && localStorage.getItem('sidebar-collapsed') === 'true'
    } catch (e) {
      return false
    }
  })
  const prevUserRef = useRef<any>(null)
  // Only subscribe to user and logout, not the entire store
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const navigate = useNavigate()

  const handleLogout = useCallback(async () => {
    await logout()
    navigate('/login')
  }, [logout, navigate])

  // Memoize onClose to prevent Sidebar re-renders - use useCallback to ensure stable reference
  const handleSidebarClose = useCallback(() => {
    onClose()
  }, [onClose])

  // Toggle sidebar behavior: on small screens open drawer, on desktop toggle collapsed state
  const handleSidebarToggle = useCallback(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      onOpen()
    } else {
      setCollapsed((s) => {
        const next = !s
        try {
          if (typeof window !== 'undefined') localStorage.setItem('sidebar-collapsed', next ? 'true' : 'false')
        } catch (e) {
          // ignore storage errors
        }
        return next
      })
    }
  }, [onOpen])

  // When user logs in (transition from null->user), default to collapsed unless explicit preference exists
  useEffect(() => {
    if (!prevUserRef.current && user) {
      try {
        if (typeof window !== 'undefined') {
          const existing = localStorage.getItem('sidebar-collapsed')
          if (existing === null) {
            localStorage.setItem('sidebar-collapsed', 'true')
            setCollapsed(true)
          }
        }
      } catch (e) {
        // ignore storage errors
      }
    }
    prevUserRef.current = user
  }, [user])

  // Navigation is now driven by user.user_routes inside Sidebar

  return (
    <Box minH="100vh" bg={bgColor}>
      <Flex>
        {/* Desktop Sidebar */}
        <Sidebar isOpen={isOpen} onClose={handleSidebarClose} collapsed={collapsed} />

        {/* Main Content */}
        <Box flex="1" bg={bgColor}>
          {/* Header */}
          <Header onToggle={handleSidebarToggle} isCollapsed={collapsed} user={user} onLogout={handleLogout} />

          {/* Page Content */}
          <Box bg={bgColor} h="calc(100vh - 85px)" maxH="calc(100vh - 85px)" overflow="hidden" position="relative">
            {children}
          </Box>
        </Box>
      </Flex>
    </Box>
  )
}

// Memoize AdminLayout to prevent unnecessary re-renders
export const AdminLayout = memo(AdminLayoutComponent)

